import {
  Home,
  Camera,
  Calendar,
  DollarSign,
  Users,
  FileText,
  Settings,
  HelpCircle,
  LogOut,
  ChevronRight,
  ChevronDown,
  Plus,
  Folder,
  CheckSquare,
  MessageSquare,
  TrendingUp,
  Image,
  Palette,
  Hammer,
  Sparkles,
  Package,
  ClipboardList,
  AlertTriangle,
  BarChart3,
  Bell,
  Search,
  Moon,
  Sun,
  Laptop,
  HardHat,
  Ruler,
  Truck,
  Eye,
  ShoppingCart,
  Zap,
  Brain,
  Calculator,
  Compass,
  Map,
  Target,
  BookOpen,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams, Form } from 'react-router'
import { Avatar, AvatarFallback, AvatarImage } from '#app/components/ui/avatar'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '#app/components/ui/sidebar'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'

// Navigation items based on user role
const getNavigationItems = (userRole?: string, projectId?: string) => {
  const baseItems = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: Home,
      badge: null,
    },
  ]

  // Project and stage items - always show but adapt URLs based on context
  const projectItems = [
    {
      title: 'My Project',
      icon: Folder,
      isCollapsible: true,
      items: [
        {
          title: 'Overview',
          url: projectId ? `/projects/${projectId}` : '/dashboard',
          icon: Eye,
        },
        {
          title: 'Current State',
          url: projectId ? `/projects/${projectId}/gallery` : '/dashboard',
          icon: Camera,
        },
        {
          title: 'Vision Board',
          url: projectId ? `/projects/${projectId}/moodboards` : '/dashboard',
          icon: Palette,
        },
        {
          title: 'Floor Plans',
          url: projectId ? `/projects/${projectId}/floorplans` : '/dashboard',
          icon: Map,
        },
      ],
    },
    {
      title: 'Journey Stages',
      icon: Compass,
      isCollapsible: true,
      items: [
        {
          title: '🔍 Orientation',
          url: projectId ? `/projects/${projectId}/orientation` : '/dashboard',
          icon: Search,
          badge: projectId ? { text: 'Active', variant: 'default' as const } : null,
        },
        {
          title: '📐 Planning',
          url: projectId ? `/projects/${projectId}/planning` : '/dashboard',
          icon: Ruler,
        },
        {
          title: '🔨 Building',
          url: projectId ? `/projects/${projectId}/building` : '/dashboard',
          icon: Hammer,
        },
        {
          title: '✨ Finishing',
          url: projectId ? `/projects/${projectId}/finishing` : '/dashboard',
          icon: Sparkles,
        },
      ],
    },
  ]

  // Smart Tools section - always available
  const smartToolsItems = [
    {
      title: 'Smart Tools',
      icon: Brain,
      isCollapsible: true,
      items: [
        {
          title: '👷 Professionals',
          url: '/professionals',
          icon: Users,
          badge: { text: 'Find', variant: 'default' as const },
        },
        {
          title: '🛒 Marketplace',
          url: '/marketplace',
          icon: ShoppingCart,
          badge: { text: 'New', variant: 'secondary' as const },
        },
        {
          title: '⚡ Energy Tracker',
          url: '/energy',
          icon: Zap,
        },
        {
          title: '🤖 AI Assistant',
          url: '/ai-assistant',
          icon: Brain,
        },
        {
          title: '💰 ROI Calculator',
          url: '/roi-calculator',
          icon: Calculator,
        },
      ],
    },
  ]

  // Management items - adapt based on project existence
  const managementItems = [
    {
      title: 'Management',
      icon: ClipboardList,
      isCollapsible: true,
      items: [
        {
          title: 'Budget',
          url: projectId ? `/projects/${projectId}/budget` : '/budget',
          icon: DollarSign,
          badge: { text: 'Track', variant: 'secondary' as const },
        },
        {
          title: 'Timeline',
          url: projectId ? `/projects/${projectId}/timeline` : '/timeline',
          icon: Calendar,
        },
        {
          title: 'Documents',
          url: projectId ? `/projects/${projectId}/documents` : '/documents',
          icon: FileText,
        },
        {
          title: 'Team',
          url: projectId ? `/projects/${projectId}/team` : '/team',
          icon: Users,
        },
      ],
    },
  ]

  // Role-specific items
  const roleItems = (() => {
    switch (userRole) {
      case 'homeowner':
        return []
      case 'contractor':
        return [
          {
            title: 'Operations',
            icon: HardHat,
            isCollapsible: true,
            items: [
              {
                title: 'Schedule',
                url: '/schedule',
                icon: Calendar,
                badge: { text: 'Today', variant: 'default' as const },
              },
              {
                title: 'Crew',
                url: '/crew',
                icon: Users,
              },
              {
                title: 'Materials',
                url: '/materials',
                icon: Package,
              },
              {
                title: 'Quality',
                url: '/quality',
                icon: CheckSquare,
              },
            ],
          },
          {
            title: 'Reporting',
            icon: BarChart3,
            isCollapsible: true,
            items: [
              {
                title: 'Daily Reports',
                url: '/reports/daily',
                icon: FileText,
              },
              {
                title: 'Issues',
                url: '/issues',
                icon: AlertTriangle,
                badge: { text: '2', variant: 'destructive' as const },
              },
              {
                title: 'Progress',
                url: '/progress',
                icon: TrendingUp,
              },
            ],
          },
        ]
      case 'architect':
        return [
          {
            title: 'Design',
            icon: Ruler,
            isCollapsible: true,
            items: [
              {
                title: 'Plans',
                url: '/plans',
                icon: FileText,
              },
              {
                title: 'Specifications',
                url: '/specifications',
                icon: ClipboardList,
              },
              {
                title: 'Materials',
                url: '/materials',
                icon: Package,
              },
              {
                title: 'Revisions',
                url: '/revisions',
                icon: TrendingUp,
                badge: { text: '3', variant: 'secondary' as const },
              },
            ],
          },
        ]
      case 'supplier':
        return [
          {
            title: 'Supply',
            icon: Truck,
            isCollapsible: true,
            items: [
              {
                title: 'Orders',
                url: '/orders',
                icon: Package,
                badge: { text: '5', variant: 'default' as const },
              },
              {
                title: 'Deliveries',
                url: '/deliveries',
                icon: Truck,
              },
              {
                title: 'Inventory',
                url: '/inventory',
                icon: ClipboardList,
              },
            ],
          },
        ]
      default:
        return []
    }
  })()

  const communityItems = [
    {
      title: 'Community',
      icon: Users,
      isCollapsible: true,
      items: [
        {
          title: 'Feed',
          url: projectId ? `/projects/${projectId}/feed` : '/feed',
          icon: TrendingUp,
        },
        {
          title: 'Stories',
          url: projectId ? `/projects/${projectId}/stories` : '/stories',
          icon: Camera,
        },
        {
          title: 'Messages',
          url: '/messages',
          icon: MessageSquare,
          badge: { text: '2', variant: 'default' as const },
        },
      ],
    },
  ]

  const bottomItems = [
    {
      title: 'Settings',
      url: '/settings',
      icon: Settings,
    },
    {
      title: 'Help & Support',
      url: '/help',
      icon: HelpCircle,
    },
  ]

  return {
    mainItems: [...baseItems, ...projectItems, ...smartToolsItems, ...managementItems, ...roleItems, ...communityItems],
    bottomItems,
  }
}

interface AppSidebarProps {
  defaultProjectId?: string
}

export function AppSidebar({ defaultProjectId }: AppSidebarProps = {}) {
  const location = useLocation()
  const params = useParams()
  const user = useOptionalUser()
  const { state, isMobile } = useSidebar()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['Project', 'Stages']))

  // Determine user role (mock for now - should come from user data)
  const userRole = user?.roles?.[0]?.name || 'homeowner'
  const projectId = params.projectId || defaultProjectId

  const { mainItems, bottomItems } = getNavigationItems(userRole, projectId)

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(title)) {
        newSet.delete(title)
      } else {
        newSet.add(title)
      }
      return newSet
    })
  }

  const isActiveUrl = (url: string) => {
    return location.pathname === url || location.pathname.startsWith(url + '/')
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Home className="size-4" />
          </div>
          {state === 'expanded' && (
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold">Woonplan</span>
              <span className="text-xs text-muted-foreground">
                {projectId ? 'Project View' : 'Dashboard'}
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => {
                const isExpanded = expandedItems.has(item.title)
                const hasSubItems = 'items' in item && item.items

                return (
                  <SidebarMenuItem key={item.title}>
                    {hasSubItems ? (
                      <>
                        <SidebarMenuButton
                          onClick={() => item.isCollapsible && toggleExpanded(item.title)}
                          className="w-full"
                        >
                          <item.icon className="size-4" />
                          <span className="flex-1">{item.title}</span>
                          {item.isCollapsible && (
                            isExpanded ? 
                              <ChevronDown className="size-4" /> : 
                              <ChevronRight className="size-4" />
                          )}
                        </SidebarMenuButton>
                        {isExpanded && (
                          <SidebarMenuSub>
                            {item.items.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActiveUrl(subItem.url)}
                                >
                                  <Link to={subItem.url}>
                                    <subItem.icon className="size-4" />
                                    <span className="flex-1">{subItem.title}</span>
                                    {subItem.badge && (
                                      <Badge 
                                        variant={subItem.badge.variant}
                                        className="ml-auto h-5 px-1.5 text-xs"
                                      >
                                        {subItem.badge.text}
                                      </Badge>
                                    )}
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        )}
                      </>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        isActive={isActiveUrl('url' in item ? item.url : '')}
                      >
                        <Link to={'url' in item ? item.url : '#'}>
                          <item.icon className="size-4" />
                          <span className="flex-1">{item.title}</span>
                          {'badge' in item && item.badge && (
                            <Badge 
                              variant={(item.badge as any).variant}
                              className="ml-auto h-5 px-1.5 text-xs"
                            >
                              {(item.badge as any).text}
                            </Badge>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Bottom Navigation */}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              {bottomItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActiveUrl(item.url)}
                  >
                    <Link to={item.url}>
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        {user && (
          <SidebarMenu>
            <SidebarMenuItem>
              <Form method="post" action="/logout">
                <SidebarMenuButton className="w-full" type="submit">
                  <Avatar className="size-6">
                    <AvatarImage src={user.image?.objectKey ? `/resources/images/${user.image.objectKey}` : undefined} />
                    <AvatarFallback className="text-xs">
                      {user.name?.split(' ').map((n: string) => n[0]).join('') || user.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  {state === 'expanded' && (
                    <>
                      <div className="flex flex-col flex-1 text-left">
                        <span className="text-sm font-medium">
                          {user.name || user.username}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {userRole}
                        </span>
                      </div>
                      <LogOut className="size-4" />
                    </>
                  )}
                </SidebarMenuButton>
              </Form>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}